import {
  BookOpen,
  Calendar,
  ClipboardList,
  Layers,
  MoreHorizontal,
  Package,
  Sprout,
  TrendingUp,
  Truck,
} from "lucide-react";
import type { ComponentType, SVGProps } from "react";

import { useIsMobile } from "../lib/useIsMobile";
import { fonts, radius, shadow, tokens } from "../styles/theme";

export type TabId =
  | "today"
  | "trays"
  | "calendar"
  | "plans"
  | "deliveries"
  | "inventory"
  | "yield"
  | "reference";

export const TAB_TITLES: Record<TabId, string> = {
  today: "Today",
  trays: "Trays",
  calendar: "Calendar",
  plans: "Plans",
  deliveries: "Deliveries",
  inventory: "Inventory",
  yield: "Yield",
  reference: "Reference",
};

type TabMeta = {
  id: TabId;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
};

export const PRIMARY_TABS: TabMeta[] = [
  { id: "today", Icon: Sprout, label: "Today" },
  { id: "trays", Icon: Layers, label: "Trays" },
  { id: "deliveries", Icon: Truck, label: "Pack" },
  { id: "inventory", Icon: Package, label: "Stock" },
];

export const SECONDARY_TABS: TabMeta[] = [
  { id: "calendar", Icon: Calendar, label: "Calendar" },
  { id: "plans", Icon: ClipboardList, label: "Plans" },
  { id: "yield", Icon: TrendingUp, label: "Yield" },
  { id: "reference", Icon: BookOpen, label: "Reference" },
];

const PRIMARY_IDS = new Set<TabId>(PRIMARY_TABS.map((t) => t.id));

export function isSecondaryTab(tab: TabId): boolean {
  return !PRIMARY_IDS.has(tab);
}

export function TabBar({
  tab,
  onTab,
  onOpenMore,
}: {
  tab: TabId;
  onTab: (t: TabId) => void;
  onOpenMore: () => void;
}) {
  const isMobile = useIsMobile();
  const secondaryActive = isSecondaryTab(tab);

  return (
    <nav
      aria-label="Primary"
      style={
        isMobile
          ? {
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              paddingBottom: "env(safe-area-inset-bottom)",
              background: tokens.card,
              borderTop: `1px solid ${tokens.line}`,
              zIndex: 20,
            }
          : {
              position: "fixed",
              bottom: "12px",
              left: "12px",
              right: "12px",
              display: "flex",
              justifyContent: "center",
              pointerEvents: "none",
              paddingBottom: "env(safe-area-inset-bottom)",
              zIndex: 20,
            }
      }
    >
      <div
        style={
          isMobile
            ? {
                display: "flex",
                gap: "2px",
                width: "100%",
                maxWidth: "520px",
                padding: "4px",
              }
            : {
                pointerEvents: "auto",
                display: "flex",
                gap: "2px",
                width: "100%",
                maxWidth: "520px",
                padding: "6px",
                background: tokens.card,
                border: `1px solid ${tokens.line}`,
                borderRadius: radius.xl,
                boxShadow: shadow.md,
              }
        }
      >
        {PRIMARY_TABS.map((t) => (
          <TabButton
            key={t.id}
            Icon={t.Icon}
            label={t.label}
            active={tab === t.id}
            iconsOnly={isMobile}
            onClick={() => onTab(t.id)}
          />
        ))}
        <TabButton
          Icon={MoreHorizontal}
          label="More"
          active={secondaryActive}
          iconsOnly={isMobile}
          onClick={onOpenMore}
        />
      </div>
    </nav>
  );
}

function TabButton({
  Icon,
  label,
  active,
  iconsOnly,
  onClick,
}: {
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  active: boolean;
  iconsOnly: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      aria-label={iconsOnly ? label : undefined}
      title={iconsOnly ? label : undefined}
      style={{
        flex: 1,
        padding: iconsOnly ? "10px 4px" : "8px 4px",
        minHeight: iconsOnly ? "48px" : "52px",
        background: active ? tokens.sage : "transparent",
        border: "none",
        borderRadius: radius.md,
        color: active ? "#fff" : tokens.muted,
        fontFamily: fonts.body,
        fontWeight: active ? 600 : 500,
        fontSize: "11px",
        letterSpacing: "0.02em",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
        transition: "background 0.18s ease, color 0.18s ease, transform 0.12s ease",
      }}
    >
      <Icon
        width={iconsOnly ? 22 : 20}
        height={iconsOnly ? 22 : 20}
        strokeWidth={active ? 2.25 : 1.75}
      />
      {!iconsOnly && label}
    </button>
  );
}
